import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const sequenceParallelComponent: ComponentDef = {
  id: 'sequence-parallel',
  name: 'Sequence Parallel',
  description: 'Parallelize along the sequence dimension to reduce activation memory.',
  category: 'parallelism',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 2048, min: 1, max: 131072, step: 1 },
    { name: 'tp_size', label: 'TP size', type: 'number', default: 4, min: 2, max: 16, step: 1 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'megatron-sp',
      options: [
        { label: 'Megatron-LM SP', value: 'megatron-sp' },
        { label: 'SP vs TP Memory', value: 'memory-comparison' },
        { label: 'DeepSpeed Ulysses', value: 'deepspeed-ulysses' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const tp_size = Number(params.tp_size);
    const variant = String(params.variant);
    const seq_local = Math.floor(seq_len / tp_size);
    const d_local = Math.floor(d_model / tp_size);

    const g = new GraphBuilder();

    if (variant === 'megatron-sp') {
      // Megatron-LM SP+TP: LayerNorm/Dropout sharded along sequence,
      // AllGather before attention, ReduceScatter after MLP
      g.input('x_seq_shard', [seq_local, d_model]);

      // SP region: LayerNorm sharded on sequence
      g.norm('norm1', 'x_seq_shard', 'LayerNorm  (SP: seq/tp_size tokens per GPU)');

      // Transition SP -> TP: AllGather to reconstruct full sequence before attention
      g.allgather('x_full', 'norm1', [seq_len, d_model]);

      // TP region: column-parallel Q/K/V
      g.linear('q', 'x_full', [d_model, d_local]);
      g.linear('k', 'x_full', [d_model, d_local]);
      g.linear('v', 'x_full', [d_model, d_local]);
      g.matmul('qk', 'q', 'k', [seq_len, seq_len]);
      g.softmax('attn_w', 'qk', -1);
      g.matmul('attn_out', 'attn_w', 'v', [seq_len, d_local]);
      g.linear('attn_proj', 'attn_out', [d_local, d_model]);

      // Transition TP -> SP: ReduceScatter
      g.reduceScatter('attn_shard', 'attn_proj', [seq_local, d_model]);

      // SP region: residual add + second LayerNorm
      g.add('add1', 'x_seq_shard', 'attn_shard');
      g.norm('norm2', 'add1', 'LayerNorm  (SP: seq/tp_size tokens per GPU)');

      // Transition SP -> TP again before MLP
      g.allgather('mlp_in', 'norm2', [seq_len, d_model]);

      // TP region: Megatron MLP pattern
      g.linear('mlp_w1', 'mlp_in', [d_model, d_local]);
      g.activation('mlp_act', 'mlp_w1', 'GeLU  (no sync between W1 and W2)');
      g.linear('mlp_w2', 'mlp_act', [d_local, d_model]);

      // Transition TP -> SP: ReduceScatter after MLP
      g.reduceScatter('mlp_shard', 'mlp_w2', [seq_local, d_model]);

      // SP region: residual add
      g.add('out', 'add1', 'mlp_shard');
      g.output('out', 'out');
    } else if (variant === 'memory-comparison') {
      // SP vs TP Memory: show memory comparison between pure TP and SP+TP
      g.input('x_full', [seq_len, d_model]);

      // Pure TP path (higher activation memory)
      g.linear('tp_norm', 'x_full', [d_model, d_model]);
      g.linear('tp_q', 'tp_norm', [d_model, d_local]);
      g.linear('tp_k', 'tp_norm', [d_model, d_local]);
      g.matmul('tp_qk', 'tp_q', 'tp_k', [seq_len, seq_len]);
      g.softmax('tp_attn', 'tp_qk', -1);
      g.linear('tp_proj', 'tp_attn', [d_local, d_model]);
      g.metric('tp_mem', 'tp_proj', `Activation: ${seq_len}×${d_model} (full seq)`);

      // SP+TP path (lower activation memory)
      g.split('sp_shard', 'x_full', [seq_local, d_model], `/${tp_size}`);
      g.norm('sp_norm', 'sp_shard', 'LayerNorm (SP)');
      g.allgather('sp_full', 'sp_norm', [seq_len, d_model]);
      g.linear('sp_q', 'sp_full', [d_model, d_local]);
      g.linear('sp_k', 'sp_full', [d_model, d_local]);
      g.matmul('sp_qk', 'sp_q', 'sp_k', [seq_len, seq_len]);
      g.softmax('sp_attn', 'sp_qk', -1);
      g.linear('sp_proj', 'sp_attn', [d_local, d_model]);
      g.reduceScatter('sp_out', 'sp_proj', [seq_local, d_model]);
      g.metric('sp_mem', 'sp_out', `Activation: ${seq_local}×${d_model} (1/${tp_size} seq)`);

      g.output('out', 'sp_out');
    } else {
      // DeepSpeed Ulysses: All2All-based sequence parallelism
      // Each GPU holds full model but 1/tp_size of the sequence
      g.input('x_shard', [seq_local, d_model]);

      // Linear projections on local sequence shard (no communication needed)
      g.linear('q_local', 'x_shard', [d_model, d_model]);
      g.linear('k_local', 'x_shard', [d_model, d_model]);
      g.linear('v_local', 'x_shard', [d_model, d_model]);

      // All2All to redistribute along head dimension
      // Each GPU gets full sequence for its assigned heads
      g.all2all('q_heads', 'q_local', [seq_len, d_local]);
      g.all2all('k_heads', 'k_local', [seq_len, d_local]);
      g.all2all('v_heads', 'v_local', [seq_len, d_local]);

      // Local attention on full sequence, subset of heads
      g.matmul('qk', 'q_heads', 'k_heads', [seq_len, seq_len]);
      g.softmax('attn_w', 'qk', -1);
      g.matmul('attn_out', 'attn_w', 'v_heads', [seq_len, d_local]);

      // All2All to redistribute back along sequence dimension
      g.all2all('attn_shard', 'attn_out', [seq_local, d_model]);

      // Output projection on local shard
      g.linear('out_proj', 'attn_shard', [d_model, d_model]);
      g.output('out', 'out_proj');
    }

    return g.build();
  }
};
